import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { companyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Building2, MapPin, FileText,
  Edit3, Save, X, Loader2, CheckCircle, Clock, XCircle
} from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-300 transition";

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value || '—'}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    APPROVED: { label: 'Approved',  color: 'bg-green-100 text-green-700',  icon: CheckCircle },
    PENDING:  { label: 'Pending',   color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    REJECTED: { label: 'Rejected',  color: 'bg-red-100 text-red-700',       icon: XCircle },
  };
  const cfg = map[status] || map.PENDING;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="h-3.5 w-3.5" /> {cfg.label}
    </span>
  );
}

export default function CompanyProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    companyName: '', contactPersonName: '',
    gstNumber: '', panNumber: '',
    address: '', city: '', state: '', pincode: '',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await companyAPI.getProfile();
      const data = res.data.data;
      setProfile(data);
      setForm({
        companyName: data.companyName || '',
        contactPersonName: data.contactPersonName || '',
        gstNumber: data.gstNumber || '',
        panNumber: data.panNumber || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await companyAPI.updateProfile(form);
      setProfile(res.data.data);
      setEditing(false);
      toast.success('Profile updated!');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const orgLabel = profile?.organizationType === 'HOSPITAL' ? 'Hospital' : 'Company';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 transition p-1 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-5">

        {/* Header card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl
                flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">{profile?.companyName}</h1>
                <p className="text-sm text-gray-500">{orgLabel} · {profile?.contactPersonName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={profile?.status} />
              {!editing && (
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 bg-teal-50 text-teal-600 border border-teal-200
                    px-4 py-2 rounded-xl text-sm font-bold hover:bg-teal-100 transition">
                  <Edit3 className="h-4 w-4" /> Edit
                </button>
              )}
            </div>
          </div>

          {profile?.status === 'REJECTED' && profile?.rejectionReason && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-sm text-red-700">
                <span className="font-bold">Rejection reason: </span>{profile.rejectionReason}
              </p>
            </div>
          )}
        </div>

        {/* Company Details */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Building2 className="h-4 w-4 text-blue-600" />
              </div>
              <h2 className="font-bold text-slate-800">Company Details</h2>
            </div>
            {editing && (
              <div className="flex gap-2">
                <button onClick={() => { setEditing(false); }}
                  className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-semibold">
                  <X className="h-4 w-4" /> Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-1.5 rounded-xl
                    text-sm font-bold hover:bg-teal-700 disabled:opacity-60 transition">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'Company Name', key: 'companyName' },
                { label: 'Contact Person', key: 'contactPersonName' },
                { label: 'GST Number', key: 'gstNumber' },
                { label: 'PAN Number', key: 'panNumber' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">{f.label}</label>
                  <input value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className={inp} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoRow label="Organisation Type" value={orgLabel} />
              <InfoRow label="Contact Person"    value={profile?.contactPersonName} />
              <InfoRow label="Email"             value={profile?.contactEmail} />
              <InfoRow label="Phone"             value={profile?.contactPhone} />
              <InfoRow label="GST Number"        value={profile?.gstNumber} />
              <InfoRow label="PAN Number"        value={profile?.panNumber} />
            </div>
          )}
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <MapPin className="h-4 w-4 text-red-500" />
            </div>
            <h2 className="font-bold text-slate-800">Address</h2>
          </div>

          {editing ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Address</label>
                <input value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Building, Street, Area"
                  className={inp} />
              </div>
              {[
                { label: 'City',    key: 'city',    placeholder: 'Mumbai' },
                { label: 'State',   key: 'state',   placeholder: 'Maharashtra' },
                { label: 'Pincode', key: 'pincode', placeholder: '400001' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">{f.label}</label>
                  <input value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className={inp} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <InfoRow label="Address" value={profile?.address} />
              </div>
              <InfoRow label="City"    value={profile?.city} />
              <InfoRow label="State"   value={profile?.state} />
              <InfoRow label="Pincode" value={profile?.pincode} />
            </div>
          )}
        </div>

        {/* Account info — read only */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 text-gray-500" />
            </div>
            <h2 className="font-bold text-slate-800">Account Info</h2>
            <span className="text-xs text-gray-400 ml-1">— read only</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoRow label="Login Phone"       value={profile?.contactPhone} />
            <InfoRow label="Login Email"       value={profile?.contactEmail} />
            <InfoRow label="Organisation Type" value={orgLabel} />
            <InfoRow label="Account Status"    value={profile?.status} />
          </div>
          <p className="text-xs text-gray-400 mt-4">
            Phone, email and organisation type cannot be changed. Contact ShramSena support if needed.
          </p>
        </div>

      </div>
    </div>
  );
}
